// src/components/Inventory.tsx

import { useCallback, useMemo, useState } from 'react'
import { Package, Puzzle, ChevronDown } from 'lucide-react'
import { collection, doc, QuerySnapshot } from 'firebase/firestore'
import { db } from '@/helpers/firebase'
import { useTonAddress } from '@tonconnect/ui-react'
import './Inventory.css'
import { OpenPackModal } from '@/screens/Modals/OpenPackModal'
import {
  Fragment,
  collectionPath as fragmentsCollectionPath,
} from 'common/schema/users/fragments'
import {
  UserCardPack,
  collectionPath as userCardPacksCollectionPath,
} from 'common/schema/users/userCardPacks'
import {
  CardPack,
  collectionPath as cardPacksCollectionPath,
} from 'common/schema/cardPacks'
import { User, documentPath as usersDocumentPath } from 'common/schema/users'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { useCachedPublicImage } from '@/helpers/urls'
import { useNftMint } from '@/hooks/useNftMint'
import { FragmentMintModal } from '@/screens/Modals/FragmentMintModal'
import { RarityType } from 'common/schema/cards'
import {
  useFirestoreCollectionCache,
  useFirestoreDocCache,
  useFirestoreMapCache,
} from '@/hooks/useFirestoreCache'

const RARITY_ORDER = {
  Legendary: 5,
  Epic: 4,
  Rare: 3,
  Uncommon: 2,
  Common: 1,
}

type CardPacksMap = { [key: string]: CardPack }

// Helper component for Fragments with cached images
const FragmentCard: React.FC<{ fragment: Fragment; onClick: () => void }> = ({
  fragment,
  onClick,
}) => {
  const cachedImageUrl = useCachedPublicImage(fragment.url)

  return (
    <div
      className={`fragment-card rarity-${fragment.rarity.toLowerCase()}`}
      onClick={onClick}
      style={{
        backgroundImage: cachedImageUrl ? `url(${cachedImageUrl})` : 'none',
      }}
    >
      <div className="fragment-content">
        <h3>{fragment.name}</h3>
        <p>{fragment.fragmentCount} fragments</p>
        <span className="fragment-rarity">{fragment.rarity}</span>
      </div>
    </div>
  )
}

// Helper component for Pack Cards with cached images
// Update the PackCard component typing
interface PackCardProps {
  pack: UserCardPack
  packDef?: CardPack
  isOpened?: boolean
  onClick?: () => void
}

const PackCard: React.FC<PackCardProps> = ({
  pack,
  packDef,
  isOpened,
  onClick,
}) => {
  const cachedImageUrl = useCachedPublicImage(packDef?.url)

  return (
    <div
      className={`pack-card ${isOpened ? 'opened' : 'unopened'}`}
      style={{
        backgroundImage: cachedImageUrl ? `url(${cachedImageUrl})` : 'none',
        backgroundSize: 'cover',
      }}
      onClick={onClick}
    >
      <div className="pack-content">
        <h3>{packDef?.category || 'Pack'}</h3>
        <p>{packDef?.collection}</p>
        <span className="pack-date">
          {new Date(pack.purchasedAt.seconds * 1000).toLocaleDateString()}
        </span>
      </div>
    </div>
  )
}

interface InventoryProps {
  isInTutorial: boolean
  defaultSection?: 'packs' | 'fragments'
  defaultExpanded?: 'unopened' | 'opened' | null
  onSectionChange?: (section: 'packs' | 'fragments') => void
}

// Use cached Firestore queries
export const Inventory: React.FC<InventoryProps> = ({
  isInTutorial,
  defaultSection = 'fragments',
  defaultExpanded = 'unopened',
  onSectionChange,
}) => {
  const [activeSection, setActiveSection] = useState<'packs' | 'fragments'>(
    isInTutorial ? 'packs' : defaultSection
  )
  const [selectedRarity, setSelectedRarity] = useState<RarityType | null>(null)
  const [expandedSection, setExpandedSection] = useState<
    'unopened' | 'opened' | null
  >(isInTutorial ? 'unopened' : defaultExpanded)
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null)
  const [isOpenModalVisible, setIsOpenModalVisible] = useState(false)
  const [selectedFragment, setSelectedFragment] = useState<Fragment | null>(
    null
  )
  const { mintNft } = useNftMint()
  const walletAddress = useTonAddress()

  // Memoize transform functions
  const transformUserCardPacks = useCallback((snapshot: QuerySnapshot) => {
    const packs: UserCardPack[] = []
    snapshot.forEach((doc) => {
      packs.push({
        ...doc.data(),
        userCardPackId: doc.id,
      } as UserCardPack)
    })
    return packs
  }, [])

  const transformFragments = useCallback((snapshot: QuerySnapshot) => {
    const frags: Fragment[] = []
    snapshot.forEach((doc) => {
      frags.push({
        ...doc.data(),
        cardId: doc.id,
      } as Fragment)
    })
    return frags.sort(
      (a, b) =>
        (RARITY_ORDER[b.rarity as keyof typeof RARITY_ORDER] || 0) -
        (RARITY_ORDER[a.rarity as keyof typeof RARITY_ORDER] || 0)
    )
  }, [])

  const transformCardPacks = useCallback((snapshot: QuerySnapshot) => {
    const packs: CardPacksMap = {}
    snapshot.forEach((doc) => {
      packs[doc.id] = {
        ...doc.data(),
        cardPackId: doc.id,
      } as CardPack
    })
    return packs
  }, [])

  // Use the hooks with memoized transform functions
  const { data: userCardPacks = [] } =
    useFirestoreCollectionCache<UserCardPack>(
      walletAddress
        ? collection(db, userCardPacksCollectionPath(walletAddress))
        : null,
      `cardPacks-${walletAddress}`,
      transformUserCardPacks,
      !!walletAddress
    )

  const { data: fragments = [] } = useFirestoreCollectionCache<Fragment>(
    walletAddress
      ? collection(db, fragmentsCollectionPath(walletAddress))
      : null,
    `fragments-${walletAddress}`,
    transformFragments,
    !!walletAddress
  )

  const { data: cardPacks = {} as CardPacksMap } =
    useFirestoreMapCache<CardPack>(
      collection(db, cardPacksCollectionPath()),
      'cardPackDefinitions',
      transformCardPacks
    )

  // Memoize filtered data
  const filteredFragments = useMemo(
    () =>
      selectedRarity
        ? fragments.filter((fragment) => fragment.rarity === selectedRarity)
        : fragments,
    [fragments, selectedRarity]
  )

  const unusedPacks = useMemo(
    () => userCardPacks.filter((pack) => pack.cardsReceived.length === 0),
    [userCardPacks]
  )

  const usedPacks = useMemo(
    () => userCardPacks.filter((pack) => pack.cardsReceived.length > 0),
    [userCardPacks]
  )

  const { data: userData } = useFirestoreDocCache<User>(
    walletAddress ? doc(db, usersDocumentPath(walletAddress)) : null,
    `userData-${walletAddress}`,
    (snapshot) => (snapshot.data() as User) || null,
    !!walletAddress
  )

  const userBalance = userData?.tokenBalance || 0
  // Remove the problematic effect and handle section changes directly
  const handleSectionChange = (section: 'packs' | 'fragments') => {
    // Don't allow changing sections during tutorial
    if (isInTutorial) return

    setActiveSection(section)
    if (onSectionChange) {
      onSectionChange(section)
    }
  }

  const handleFragmentClick = (fragment: Fragment) => {
    setSelectedFragment(fragment)
  }

  const handlePackClick = (packId: string) => {
    setSelectedPackId(packId)
    setIsOpenModalVisible(true)
  }

  const handleMint = async () => {
    if (!selectedFragment) return
    try {
      await mintNft()
      setSelectedFragment(null)
    } catch (err) {
      console.error('Failed to mint NFT:', err)
    }
  }

  const handlePackOpened = () => {
    // Modal will handle state updates through Firebase listeners
  }

  return (
    <ProtectedRoute>
      <div className="inventory-container">
        <div className="token-balance">
          <span className="token-amount">{userBalance}</span>
          <span className="token-label">CARD token</span>
        </div>

        <div className="section-nav">
          <button
            className={`section-btn ${activeSection === 'fragments' ? 'active' : ''}`}
            onClick={() => handleSectionChange('fragments')}
            disabled={isInTutorial} // Optionally disable during tutorial
          >
            <Puzzle size={20} />
            Fragments
          </button>
          <button
            className={`section-btn packs-section ${activeSection === 'packs' ? 'active' : ''}`}
            onClick={() => handleSectionChange('packs')}
            disabled={isInTutorial} // Optionally disable during tutorial
          >
            <Package size={20} />
            Card Packs
          </button>
        </div>

        {activeSection === 'packs' && (
          <>
            <div className="inventory-section">
              <div
                className="section-header"
                onClick={() =>
                  setExpandedSection(
                    expandedSection === 'unopened' ? null : 'unopened'
                  )
                }
              >
                <h2>Unopened Card Packs ({unusedPacks.length})</h2>
                <ChevronDown
                  className={`chevron ${expandedSection === 'unopened' ? 'expanded' : ''}`}
                  size={20}
                />
              </div>
              {expandedSection === 'unopened' && (
                <div className="packs-grid">
                  {unusedPacks.map((pack) => (
                    <PackCard
                      key={pack.userCardPackId}
                      pack={pack}
                      packDef={cardPacks?.[pack.cardPackId]}
                      onClick={() => handlePackClick(pack.userCardPackId)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="inventory-section">
              <div
                className="section-header"
                onClick={() =>
                  setExpandedSection(
                    expandedSection === 'opened' ? null : 'opened'
                  )
                }
              >
                <h2>Opened Card Packs ({usedPacks.length})</h2>
                <ChevronDown
                  className={`chevron ${expandedSection === 'opened' ? 'expanded' : ''}`}
                  size={20}
                />
              </div>
              {expandedSection === 'opened' && (
                <div className="packs-grid">
                  {usedPacks.map((pack) => (
                    <PackCard
                      key={pack.userCardPackId}
                      pack={pack}
                      packDef={cardPacks?.[pack.cardPackId]}
                      isOpened
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeSection === 'fragments' && (
          <>
            <div className="rarity-filter">
              {Object.keys(RARITY_ORDER).map((rarity) => (
                <button
                  key={rarity}
                  className={`rarity-btn ${selectedRarity === rarity ? 'active' : ''}`}
                  onClick={() =>
                    setSelectedRarity(
                      selectedRarity === (rarity as RarityType)
                        ? null
                        : (rarity as RarityType)
                    )
                  }
                >
                  {rarity}
                </button>
              ))}
            </div>

            <div className="fragments-grid">
              {filteredFragments.map((fragment) => (
                <FragmentCard
                  key={fragment.cardId}
                  fragment={fragment}
                  onClick={() => handleFragmentClick(fragment)}
                />
              ))}
            </div>
          </>
        )}

        {selectedFragment && (
          <FragmentMintModal
            fragment={selectedFragment}
            isOpen={!!selectedFragment}
            onClose={() => setSelectedFragment(null)}
            onMint={handleMint}
          />
        )}

        <OpenPackModal
          isOpen={isOpenModalVisible}
          onClose={() => {
            setIsOpenModalVisible(false)
            setSelectedPackId(null)
          }}
          packId={selectedPackId || ''}
          onSuccess={handlePackOpened}
        />
      </div>
    </ProtectedRoute>
  )
}
